<template>
	<PublicPage wide current="/about-us" eyebrow="ABOUT US" :title="['A bold idea.', 'Built to last.']" lead="Paydeck began with one person, one rent payment and a simple question: why can't I pay this the way I want to?">
		<div class="trust about-story">
			<!-- 01 The kickstart -->
			<section class="trust-section tone-white" aria-labelledby="about-start">
				<div class="trust-head">
					<div><PdEyebrow>01 / The kickstart</PdEyebrow><h2 id="about-start">It started with a rent payment.</h2></div>
					<p>When Saurabh, the founder of Paydeck, wanted to pay his home rent and society maintenance on his credit card, there was not a single way to do it anywhere in the country. So he built one.</p>
				</div>
				<div class="trust-bento">
					<PdTile tone="ink" pad="lg" rings class="fact-tile quote-tile span-7 rows-2">
						<Quote :size="40" :stroke-width="1.6" class="quote-mark" aria-hidden="true" />
						<p class="quote-text">There was not a single avenue of payment available in the country to pay rent on a credit card.</p>
						<p class="quote-by"><PdAvatar name="Saurabh Suman" :src="founders[0].photo" tone="orange" size="md" class="person-photo" /> <span><strong>Saurabh Suman</strong><small>Founder, Paydeck</small></span></p>
					</PdTile>
					<PdTile tone="orange" pad="lg" class="fact-tile span-5">
						<PdEyebrow tone="ink">Where it began</PdEyebrow>
						<div><strong class="fact-value fact-value-sm pd-num">2014</strong><p class="fact-label">One rent payment, one missing option</p></div>
					</PdTile>
					<PdTile tone="cream" pad="lg" class="fact-tile span-5">
						<PdEyebrow tone="muted">Where it is today</PdEyebrow>
						<div class="about-stats">
							<div><strong class="pd-num">15.1L+</strong><span>Paydeck users</span></div>
							<div><strong class="pd-num">₹100Bn+</strong><span>Processed transactions</span></div>
						</div>
					</PdTile>
				</div>
			</section>

			<!-- 02 What we believe -->
			<section class="trust-section tone-cream" aria-labelledby="about-belief">
				<div class="trust-head">
					<div><PdEyebrow>02 / What we believe</PdEyebrow><h2 id="about-belief">Your card. Your money. Your call.</h2></div>
					<p>Life comes with payments: a home, an education, the people who build and fix things. They should be paid the way that works for you, not only the way the recipient happens to accept.</p>
				</div>
				<div class="trust-bento">
					<PdTile tone="orange" pad="lg" rings class="fact-tile manifesto-tile span-12">
						<p class="manifesto">It's your credit card. It's your money. And so the decision to pay should be yours.</p>
						<PdEyebrow tone="ink">The Paydeck idea, since day one</PdEyebrow>
					</PdTile>
				</div>
			</section>

			<!-- 03 How a card payment works -->
			<section class="trust-section tone-white" aria-labelledby="about-sides">
				<div class="trust-head">
					<div><PdEyebrow>03 / How it works</PdEyebrow><h2 id="about-sides">Simple card payments. Nothing more.</h2></div>
					<p>You pay with your credit or debit card, and the recipient is paid into their bank account as usual. Nobody needs a card machine, an app or a new account.</p>
				</div>
				<div class="trust-bento">
					<PdTile tone="cream" pad="lg" class="side-tile span-6">
						<span class="fact-icon"><CreditCard :size="26" :stroke-width="2" aria-hidden="true" /></span>
						<h3>How you pay</h3>
						<p>Rent, housing maintenance, fees or any of life's essential payments, paid with the card already in your wallet.</p>
						<ol class="approve-steps">
							<li><b class="pd-num">1</b><span>Ask for the recipient's account number, IFSC code and exact account name.</span></li>
							<li><b class="pd-num">2</b><span>Review the details and pay with your card.</span></li>
							<li><b class="pd-num">3</b><span>Paydeck takes care of the rest.</span></li>
						</ol>
					</PdTile>
					<PdTile tone="ink" pad="lg" class="side-tile span-6">
						<span class="fact-icon"><Landmark :size="26" :stroke-width="2" aria-hidden="true" /></span>
						<h3>What the recipient gets</h3>
						<p>Your landlord, school or contractor receives the money in their own bank account, just like any bank transfer. They do not have to do anything differently.</p>
						<ul class="approve-steps recipient-points">
							<li><b><Check :size="16" :stroke-width="3" aria-hidden="true" /></b><span>Paid straight into their own bank account.</span></li>
							<li><b><Check :size="16" :stroke-width="3" aria-hidden="true" /></b><span>No card machine, app or sign up needed.</span></li>
							<li><b><Check :size="16" :stroke-width="3" aria-hidden="true" /></b><span>You keep a receipt and reference for every payment.</span></li>
						</ul>
					</PdTile>
				</div>
			</section>

			<!-- 04 Card benefits -->
			<section class="trust-section tone-cream" aria-labelledby="about-benefits">
				<div class="trust-head">
					<div><PdEyebrow>04 / Smarter with a card</PdEyebrow><h2 id="about-benefits">Make your card work harder.</h2></div>
					<p>Our customers know how to use their credit cards well. Used wisely, a card can offset much of the cost of paying through Paydeck.</p>
				</div>
				<div class="trust-bento">
					<PdTile v-for="(item, i) in benefits" :key="item.title" :tone="i === 1 ? 'orange' : 'white'" pad="lg" class="benefit-tile span-4">
						<span class="fact-icon"><component :is="icons[item.icon]" :size="26" :stroke-width="2" aria-hidden="true" /></span>
						<div><h3>{{ item.title }}</h3><p>{{ item.body }}</p></div>
					</PdTile>
				</div>
				<p class="about-footnote">Rewards, interest free periods and tax treatment depend on your card issuer and your own circumstances.</p>
			</section>

			<!-- 05 People -->
			<section class="trust-section tone-white" aria-labelledby="about-people">
				<div class="trust-head">
					<div><PdEyebrow>05 / The people</PdEyebrow><h2 id="about-people">The people behind Paydeck.</h2></div>
					<p>A small team that has stayed close to one idea for more than a decade, and still answers to the customers who trusted it first.</p>
				</div>
				<div class="trust-bento">
					<PdTile v-for="person in founders" :key="person.name" tone="ink" pad="lg" class="person-tile person-founder span-6">
						<div class="person-top"><PdAvatar :name="person.name" :src="person.photo" tone="orange" size="xl" class="person-photo" /><PdEyebrow tone="light">Founding member</PdEyebrow></div>
						<div>
							<h3>{{ person.name }}</h3>
							<p class="person-role">{{ person.role }}</p>
							<p>{{ person.body }}</p>
						</div>
						<PdArrowLink :href="person.href" tone="light" target="_blank" rel="noopener noreferrer">Profile</PdArrowLink>
					</PdTile>
					<PdTile v-for="person in team" :key="person.name" tone="cream" pad="lg" class="person-tile span-3">
						<PdAvatar :name="person.name" :src="person.photo" tone="peach" size="xl" class="person-photo" />
						<div>
							<h3>{{ person.name }}</h3>
							<p class="person-role">{{ person.role }}</p>
							<p>{{ person.body }}</p>
						</div>
					</PdTile>
					<PdTile tone="orange" pad="lg" class="person-tile span-3">
						<span class="bot-avatar"><Bot :size="34" :stroke-width="1.8" aria-hidden="true" /></span>
						<div>
							<h3>AbhiSek</h3>
							<p class="person-role">Paydeck's support bot</p>
							<p>Named in honour of Avishek, our first and longest serving customer support member.</p>
						</div>
					</PdTile>
				</div>
			</section>

			<!-- 06 Press -->
			<section class="trust-section tone-ink" aria-labelledby="about-press">
				<div class="trust-head">
					<div><PdEyebrow tone="light">06 / In the press</PdEyebrow><h2 id="about-press">What they wrote about us.</h2></div>
					<p>Paydeck has been covered by India's startup and business press since 2016. Here is the story in their words.</p>
				</div>
				<div class="trust-bento">
					<a v-for="(item, i) in press" :key="item.href" :class="['press-tile', i < 2 ? 'span-6' : 'span-4', { 'is-lead': i === 0 }]" :href="item.href" target="_blank" rel="noopener noreferrer">
						<span :class="['press-logo', { 'is-dark': item.logoDark }]">
							<img :src="item.logo" :alt="item.outlet" :class="{ 'is-mark': item.logoMark }" loading="lazy" />
							<span v-if="item.logoMark" class="press-logo-name">{{ item.outlet }}</span>
						</span>
						<span class="press-headline">“{{ item.headline }}”</span>
						<span class="media-read">Read on {{ item.outlet }} <ArrowUpRight :size="16" :stroke-width="2.4" aria-hidden="true" /></span>
					</a>
					<div class="press-tile press-more span-4">
						<span class="press-outlet">And counting</span>
						<span class="press-headline">Seven publications, one story: making card payments possible where cards were never accepted.</span>
					</div>
				</div>
			</section>

			<!-- 07 Next chapter -->
			<section class="trust-section tone-white" aria-labelledby="about-next">
				<div class="trust-bento">
					<PdTile tone="white" pad="lg" class="next-tile next-neon span-7">
						<PdEyebrow tone="ink">07 / The next chapter</PdEyebrow>
						<h2 id="about-next">From India to Thailand.</h2>
						<p>More than a decade of experience in India is shaping a new chapter. Paydeck Thailand is preparing a separate experience for life and payments there.</p>
						<PdArrowLink tone="ink" href="https://www.paydeck.in.th/en" target="_blank" rel="noopener noreferrer" underline>Explore Paydeck Thailand</PdArrowLink>
					</PdTile>
					<PdTile tone="ink" pad="lg" rings class="next-tile span-5">
						<PdEyebrow tone="light">Start here</PdEyebrow>
						<h2>Pay life's essentials your way.</h2>
						<div class="next-actions">
							<PdButton :href="registerUrl" :icon-right="ArrowUpRight">Create an account</PdButton>
							<PdArrowLink href="/payment-types" tone="light">See what you can pay</PdArrowLink>
						</div>
					</PdTile>
				</div>
			</section>
		</div>
	</PublicPage>
</template>

<script setup>
import { ArrowUpRight, Bot, BriefcaseBusiness, CalendarClock, Check, CreditCard, Gift, Landmark, Quote } from "lucide-vue-next";
import PdTile from "@paydeck-ui/components/PdTile.vue";
import PdEyebrow from "@paydeck-ui/components/PdEyebrow.vue";
import PdAvatar from "@paydeck-ui/components/PdAvatar.vue";
import PdArrowLink from "@paydeck-ui/components/PdArrowLink.vue";
import PdButton from "@paydeck-ui/components/PdButton.vue";
import PublicPage from "@/components/site/PublicPage.vue";
import { benefits, founders, team, press } from "@/content/about";
import { registerUrl } from "@/content/site";

const icons = { Gift, CalendarClock, BriefcaseBusiness };
</script>
